/**
 * ACADS — the Kolkata tuition business (Theatre Road + Salt Lake).
 *
 * Alfred owns fee entry from September 2026. Three tabs: collect a fee, then
 * read it back teacher-wise and day-wise. A wrong payment is corrected on its
 * line in Daily receipts, and a student is edited from their name on the
 * teacher sheet, so there is no separate entry grid or student list. The month
 * dashboard and the sign-off live on the HQ page (AcadsHq).
 *
 * The old Reconciliation / Receipts / Teacher Payouts tabs read the parked
 * Google-Sheets mirror. They are not shown here — the mirror stops at August
 * 2026 and putting stale sheet numbers beside live entry numbers is how people
 * end up reconciling the wrong thing. `services/sync.py` still exists for that
 * history, invoked deliberately, never on a schedule.
 */
import { Paper, Tab, Tabs, Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'

import { fetchAcadsTeachers } from 'api'

import {
  CollectTab,
  RegisterTab,
  TeacherPicker,
  TeacherSheetTab
} from './AcadsCollect'
import { useStyles } from './AcadsEntry'

const TAB_KEY = 'acads.tab'

const TABS = [
  { id: 'collect', label: 'Collect a fee', teacherFilter: true },
  { id: 'sheet', label: 'Collection by teacher', teacherFilter: true },
  { id: 'receipts', label: 'Daily receipts', teacherFilter: false }
]

// Entry opens on the current month, but never before the cutover — landing on a
// read-only month would look broken rather than deliberate.
const ENTRY_START = '2026-09'

const openingMonth = () => {
  const now = new Date()
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    '0'
  )}`
  return current < ENTRY_START ? ENTRY_START : current
}

const Acads = () => {
  const classes = useStyles()
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem(TAB_KEY)
    return TABS.some((t) => t.id === saved) ? saved : 'collect'
  })
  const [teacher, setTeacher] = useState('')
  const [teachers, setTeachers] = useState([])
  // Where a tab draws its own filters, so they sit in the top bar on every
  // tab instead of somewhere different on each.
  const [slot, setSlot] = useState(null)

  useEffect(() => {
    localStorage.setItem(TAB_KEY, tab)
  }, [tab])

  const loadTeachers = useCallback(() => {
    fetchAcadsTeachers()
      .then(setTeachers)
      .catch(() => setTeachers([]))
  }, [])

  useEffect(loadTeachers, [loadTeachers])

  // A due cell on the teacher sheet opens that student in Collect.
  const [collectFor, setCollectFor] = useState('')
  const openCollect = useCallback((enrolment) => {
    setCollectFor(enrolment)
    setTab('collect')
  }, [])
  const clearCollectFor = useCallback(() => setCollectFor(''), [])

  const active = TABS.find((t) => t.id === tab)

  return (
    <Paper style={{ padding: 16 }} elevation={0}>
      <div className={classes.toolbar}>
        <Typography variant="h6">ACADS — Kolkata Tuition</Typography>
        {active.teacherFilter && (
          <TeacherPicker
            teacher={teacher}
            teachers={teachers}
            onPick={setTeacher}
            onTeachersChanged={loadTeachers}
          />
        )}
        <div
          ref={setSlot}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            marginLeft: 'auto'
          }}
        />
      </div>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        style={{ marginBottom: 16 }}
      >
        {TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>

      {tab === 'collect' && (
        <CollectTab
          teacher={teacher}
          teachers={teachers}
          onTeachersChanged={loadTeachers}
          open={collectFor}
          onOpened={clearCollectFor}
          onSeeReceipts={() => setTab('receipts')}
        />
      )}
      {tab === 'sheet' && (
        <TeacherSheetTab
          teacher={teacher}
          teachers={teachers}
          onCollect={openCollect}
          onStudentsChanged={loadTeachers}
        />
      )}
      {tab === 'receipts' && (
        <RegisterTab
          filterSlot={slot}
          teachers={teachers}
          onStudentsChanged={loadTeachers}
        />
      )}
    </Paper>
  )
}

export { ENTRY_START, openingMonth }
export default Acads
